'use client';

import { useEffect, useState } from 'react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  type RowSelectionState,
  useReactTable,
} from '@tanstack/react-table';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { cn } from '@/shared/utils/cn';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enablePagination?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  showVerticalBorder?: boolean;
  isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enablePagination = true,
  onRowSelectionChange,
  showVerticalBorder = false,
  isLoading = false,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  // 선택된 행이 변경될 때 부모 컴포넌트에 알림
  useEffect(() => {
    if (onRowSelectionChange) {
      const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
      onRowSelectionChange(selectedRows);
    }
  }, [rowSelection]);

  return (
    <div className="flex-1 flex flex-col rounded-md border overflow-auto bg-white">
      <Table>
        <TableHeader className="bg-gray-100 sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <TableHead
                    className={cn(
                      showVerticalBorder && index < headerGroup.headers.length - 1 && 'border-r'
                    )}
                    key={header.id}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="bg-white h-full">
          {isLoading
            ? // 로딩 중일 때 스켈레톤 표시
              Array.from({ length: 5 }, (_, i) => `skeleton-row-${i}`).map((rowKey) => (
                <TableRow key={rowKey}>
                  {table.getHeaderGroups()[0]?.headers.map((header) => (
                    <TableCell
                      className={cn(showVerticalBorder && 'border-r', 'px-4')}
                      key={`${rowKey}-${header.id}`}
                    >
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {!isLoading && table.getRowModel().rows?.length > 0
            ? table.getRowModel().rows.map((row, rowIndex) => (
                <TableRow
                  className={cn(rowIndex === table.getRowModel().rows.length - 1 && '!border-b')}
                  data-state={row.getIsSelected() && 'selected'}
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      className={cn(
                        showVerticalBorder && index < row.getVisibleCells().length - 1 && 'border-r'
                      )}
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
          {!isLoading && table.getRowModel().rows?.length === 0 ? (
            <TableRow>
              <TableCell
                className="h-24 text-center"
                colSpan={columns.length}
              >
                데이터가 없습니다.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}
